import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {IntersectionObserver | undefined} */
  #productMotionObserver;

  /** @type {MutationObserver | undefined} */
  #productMotionListObserver;

  /** @type {MutationObserver | undefined} */
  #productMotionRootObserver;

  /** @type {WeakSet<Element>} */
  #motionCards = new WeakSet();

  /** @type {number | undefined} */
  #motionReadyFrame;

  /** @type {number | undefined} */
  #motionObserveFrame;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#initializeProductMotion();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#destroyProductMotion();
  }

  /**
   * Enables the lightweight card entrance used only by /collections/all.
   * Cards added by filtering or infinite pagination are observed as they arrive.
   */
  #initializeProductMotion() {
    if (
      !this.hasAttribute('data-cg-all-products-motion') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      return;
    }

    this.#productMotionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.classList.toggle('cg-product-card--visible', entry.isIntersecting);
        }
      },
      { rootMargin: '80px 0px', threshold: 0.05 }
    );

    this.#productMotionListObserver = new MutationObserver((records) => {
      const addedProductCard = records.some((record) =>
        Array.from(record.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches('.product-grid__item') || node.querySelector('.product-grid__item'))
        )
      );

      if (addedProductCard) this.#scheduleMotionCards();
    });
    this.#productMotionListObserver.observe(this, { childList: true, subtree: true });

    // Section morphing restores server-rendered classes on the root. Re-apply the
    // JS-ready class and re-observe reused cards so visible entries cannot stay hidden.
    this.#productMotionRootObserver = new MutationObserver(() => {
      if (this.classList.contains('cg-all-products-motion--ready')) return;

      this.classList.add('cg-all-products-motion--ready');
      this.#scheduleMotionCards(true);
    });
    this.#productMotionRootObserver.observe(this, { attributes: true, attributeFilter: ['class'] });

    this.addEventListener('focusin', this.#handleProductMotionFocus);
    this.classList.add('cg-all-products-motion--ready');
    this.#scheduleMotionCards(true);
  }

  /**
   * Waits for a hidden frame before observing, which guarantees the entrance
   * transition without forcing synchronous layout.
   * @param {boolean} resetAll
   */
  #scheduleMotionCards(resetAll = false) {
    if (!this.#productMotionObserver) return;

    if (resetAll) {
      this.#productMotionObserver.disconnect();
      this.#motionCards = new WeakSet();
      this.querySelectorAll('.product-grid > .product-grid__item').forEach((card) => {
        card.classList.remove('cg-product-card--visible');
      });
    }

    if (this.#motionReadyFrame !== undefined) cancelAnimationFrame(this.#motionReadyFrame);
    if (this.#motionObserveFrame !== undefined) cancelAnimationFrame(this.#motionObserveFrame);

    this.#motionReadyFrame = requestAnimationFrame(() => {
      this.#motionReadyFrame = undefined;
      this.#motionObserveFrame = requestAnimationFrame(() => {
        this.#motionObserveFrame = undefined;
        this.#observeMotionCards();
      });
    });
  }

  #observeMotionCards() {
    if (!this.#productMotionObserver || !this.isConnected) return;

    this.querySelectorAll('.product-grid > .product-grid__item').forEach((card) => {
      if (this.#motionCards.has(card)) return;

      this.#motionCards.add(card);
      this.#productMotionObserver?.observe(card);
    });
  }

  /** @param {FocusEvent} event */
  #handleProductMotionFocus = (event) => {
    if (!(event.target instanceof Element)) return;

    event.target.closest('.product-grid__item')?.classList.add('cg-product-card--visible');
  };

  #destroyProductMotion() {
    this.#productMotionObserver?.disconnect();
    this.#productMotionListObserver?.disconnect();
    this.#productMotionRootObserver?.disconnect();
    this.removeEventListener('focusin', this.#handleProductMotionFocus);

    if (this.#motionReadyFrame !== undefined) cancelAnimationFrame(this.#motionReadyFrame);
    if (this.#motionObserveFrame !== undefined) cancelAnimationFrame(this.#motionObserveFrame);

    this.#productMotionObserver = undefined;
    this.#productMotionListObserver = undefined;
    this.#productMotionRootObserver = undefined;
    this.#motionReadyFrame = undefined;
    this.#motionObserveFrame = undefined;
    this.#motionCards = new WeakSet();
    this.classList.remove('cg-all-products-motion--ready');
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
